import React from "react"
import { Card, CardContent, CardActions, Typography, Chip, Box, Button, Rating } from "@mui/material"
import { LocationOn, Accessible, Wc, Visibility, ChildCare, HealthAndSafety, Pets, Vaccines } from "@mui/icons-material"
import { formatDistance } from "../utils/distanceUtils"

// Convert camelCase to Title Case
const camelToTitle = str => {
  // Insert space before capital letters and capitalize first letter
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

// Map amenity keys to icons
const getAmenityIcon = amenityKey => {
  const iconMap = {
    babyChangingTable: ChildCare,
    nursingRoom: HealthAndSafety,
    petRelief: Pets,
    sharpsReceptacle: Vaccines,
  }
  return iconMap[amenityKey] || null
}

const BathroomCard = ({ bathroom, onViewDetails, showDistance = false }) => {
  const { name, address, ratings, reviewCount, distance, genderType, amenities, photos } = bathroom

  // Extract rating and accessibility from ratings object
  const rating = ratings?.overall
  const accessibility = ratings?.accessibility

  const getGenderIcon = gender => {
    switch (gender) {
      case "men":
        return "♂️"
      case "women":
        return "♀️"
      case "unisex":
        return "⚧️"
      default:
        return "🚻"
    }
  }

  const getGenderColor = gender => {
    switch (gender) {
      case "men":
        return "primary"
      case "women":
        return "secondary"
      case "unisex":
        return "success"
      default:
        return "default"
    }
  }

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: "bold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {name || "Unnamed Location"}
          </Typography>
          {showDistance && distance !== undefined && (
            <Chip size="small" label={formatDistance(distance)} color="primary" variant="outlined" sx={{ flexShrink: 0 }} />
          )}
        </Box>

        {/* Photo */}
        {photos && photos.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <img
              src={photos[0]}
              alt={name || "Bathroom"}
              style={{
                width: "100%",
                height: "150px",
                objectFit: "cover",
                borderRadius: "8px",
                backgroundColor: "#f5f5f5",
              }}
              onError={e => {
                e.target.style.display = "none"
              }}
            />
          </Box>
        )}

        {/* Address */}
        {address && (
          <Box display="flex" alignItems="center" mb={1}>
            <LocationOn fontSize="small" color="action" sx={{ mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {address}
            </Typography>
          </Box>
        )}

        {/* Rating */}
        {rating && (
          <Box display="flex" alignItems="center" mb={1}>
            <Rating value={rating} precision={0.1} size="small" readOnly />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {rating.toFixed(1)} ({reviewCount || 0} reviews)
            </Typography>
          </Box>
        )}

        {/* Tags */}
        <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
          {/* Gender Type */}
          <Chip
            size="small"
            icon={<Wc />}
            label={`${getGenderIcon(genderType)} ${genderType || "Unisex"}`}
            color={getGenderColor(genderType)}
            variant="outlined"
          />

          {/* Accessibility */}
          {accessibility && accessibility > 3 && <Chip size="small" icon={<Accessible />} label="Accessible" color="info" variant="outlined" />}

          {/* Photo indicator */}
          {photos && photos.length > 0 && <Chip size="small" label={`${photos.length} photo${photos.length !== 1 ? "s" : ""}`} variant="outlined" />}
        </Box>

        {/* Amenities */}
        {amenities && Object.entries(amenities).filter(([, value]) => value === true).length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Amenities:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {Object.entries(amenities)
                .filter(([, value]) => value === true)
                .slice(0, 3)
                .map(([amenity], index) => {
                  const IconComponent = getAmenityIcon(amenity)
                  return (
                    <Chip
                      key={index}
                      size="small"
                      icon={IconComponent ? <IconComponent /> : undefined}
                      label={camelToTitle(amenity)}
                      variant="outlined"
                      sx={{ fontSize: "0.8rem", height: "20px", padding: "12px 8px" }}
                    />
                  )
                })}
              {Object.entries(amenities).filter(([, value]) => value === true).length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  +{Object.entries(amenities).filter(([, value]) => value === true).length - 3} more
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>

      <CardActions>
        <Button size="small" startIcon={<Visibility />} onClick={() => onViewDetails(bathroom)} fullWidth>
          View Details
        </Button>
      </CardActions>
    </Card>
  )
}

export default BathroomCard
